import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";

const ADD = "__add__";
const DEFAULTS = ["Bottle", "Can", "Tin", "Jar", "Bag", "Pack", "Tub", "Piece", "Other"];
let cache = null;
const listeners = new Set();

export function useItemTypes() {
  const [types, setTypes] = useState(cache || DEFAULTS);
  useEffect(() => {
    listeners.add(setTypes);
    if (!cache) api.get("/item-types").then((r) => { cache = r.data.item_types; listeners.forEach((fn) => fn(cache)); }).catch(() => {});
    return () => listeners.delete(setTypes);
  }, []);
  const add = async (t) => {
    const r = await api.post("/item-types", { unit: t });
    cache = r.data.item_types;
    listeners.forEach((fn) => fn(cache));
  };
  return { types, add };
}

export default function ItemTypeSelect({ value, onChange, className = "", testId }) {
  const { types, add } = useItemTypes();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const match = value ? types.find((t) => t.toLowerCase() === value.toLowerCase()) : null;
  const selected = match || value || "";
  const options = value && !match ? [value, ...types] : types;

  const save = async () => {
    const t = draft.trim();
    if (!t) return setAdding(false);
    try { await add(t); onChange(t); setAdding(false); setDraft(""); } catch (e) { toast.error(errorMessage(e)); }
  };

  if (adding) {
    return (
      <div className="flex items-center gap-1">
        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. Sachet"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") setAdding(false); }}
          data-testid={`${testId}-new`} className="w-full min-w-[70px] rounded border border-emerald-400 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <button type="button" onClick={save} className="p-1 text-emerald-700" data-testid={`${testId}-save`}><Check className="h-4 w-4" /></button>
        <button type="button" onClick={() => setAdding(false)} className="p-1 text-slate-400" data-testid={`${testId}-cancel`}><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <select value={selected} data-testid={testId}
      onChange={(e) => { if (e.target.value === ADD) setAdding(true); else onChange(e.target.value); }}
      className={`w-full rounded border px-1.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${className || "border-slate-200 bg-white"}`}>
      <option value="">—</option>
      {options.map((t) => <option key={t} value={t}>{t}</option>)}
      <option value={ADD}>+ Add type…</option>
    </select>
  );
}
